import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash2, X, ShoppingBag, CreditCard, Wallet, Layers } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${(n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORIES = ["Fletes", "Servicios", "Refacciones", "Gasolina", "Empaque", "Mantenimiento", "Papelería", "Retornables", "Otros"];

const CONSOLIDATED_CATEGORY_LABEL = {
  reception: "Leche (MP)",
  insumo: "Insumo",
  purchase: "Compra",
};

export default function Compras() {
  const [tab, setTab] = useState("compras");
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [retTypes, setRetTypes] = useState([]);
  const [consolidated, setConsolidated] = useState({ items: [], total_amount: 0, total_balance: 0 });
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const empty = { supplier_id: "", supplier_name: "", category: "Fletes", description: "", amount: "", purchase_date: new Date().toISOString().slice(0, 10), payment_method: "contado", credit_days: 0, invoice_number: "", notes: "", returnable_type_code: "", returnable_qty: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [c, s, rt, cons] = await Promise.all([api.get("/purchases"), api.get("/suppliers"), api.get("/returnables/types"), api.get("/purchases/consolidated")]);
    setItems(c.data);
    setSuppliers(s.data.sort((a, b) => a.name.localeCompare(b.name)));
    setRetTypes(rt.data);
    setConsolidated(cons.data);
  };
  useEffect(() => { load(); }, []);

  const pickSupplier = (id) => {
    const s = suppliers.find((x) => x.id === id);
    if (!s) return;
    setForm((f) => ({ ...f, supplier_id: s.id, supplier_name: s.name, credit_days: s.default_credit_days || 0, payment_method: (s.default_credit_days || 0) > 0 ? "credito" : "contado" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.supplier_name.trim()) return toast.error("Selecciona un proveedor o captura nombre");
    if (!form.description.trim()) return toast.error("Captura una descripción");
    if (!(parseFloat(form.amount) > 0)) return toast.error("Monto inválido");
    try {
      const payload = { ...form, amount: parseFloat(form.amount), credit_days: form.payment_method === "credito" ? parseInt(form.credit_days) || 0 : 0, supplier_id: form.supplier_id || null, returnable_type_code: form.category === "Retornables" ? form.returnable_type_code : null, returnable_qty: form.category === "Retornables" ? parseInt(form.returnable_qty) || 0 : 0 };
      if (form.category === "Retornables" && (!payload.returnable_type_code || payload.returnable_qty <= 0)) return toast.error("Selecciona tipo y cantidad de retornables");
      const { data } = await api.post("/purchases", payload);
      toast.success(`Compra ${data.folio} registrada · ${money(data.amount)}${data.balance > 0 ? ` · saldo ${money(data.balance)}` : ""}`);
      setForm(empty);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="compras-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 dark:text-stone-100">Compras</h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Fletes, servicios, refacciones, gasolina y retornables. También puedes ver el consolidado de todas las compras del sistema (leche, crema/cuajada, insumos y éstas).</p>
        </div>
        {!open && <Button onClick={() => setOpen(true)} data-testid="new-purchase-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Plus className="w-4 h-4 mr-1.5" /> Nueva compra</Button>}
      </div>

      {open && (
        <Card className="p-6 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-semibold text-lg text-stone-900 dark:text-stone-100">Nueva compra</h2>
            <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Proveedor *</Label>
                <Select value={form.supplier_id || ""} onValueChange={pickSupplier}>
                  <SelectTrigger className="mt-1.5" data-testid="purchase-supplier-select"><SelectValue placeholder="Elegir proveedor…" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{(s.types || []).length ? ` · ${(s.types || []).join(", ")}` : ""}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value, supplier_id: "" })} className="mt-1.5" placeholder="… o captura nombre libre" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Fecha</Label>
                <Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="purchase-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Descripción *</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" data-testid="purchase-description" placeholder="Ej. Flete Ciudad de México, refacciones bomba de vacío, etc." />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Monto *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" data-testid="purchase-amount" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Forma de pago</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="purchase-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contado">Contado</SelectItem>
                    <SelectItem value="credito">Crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Días crédito</Label>
                <Input type="number" min="0" disabled={form.payment_method === "contado"} value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Factura #</Label>
                <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="mt-1.5" />
              </div>
              {form.category === "Retornables" && (
                <>
                  <div className="md:col-span-2 p-3 rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
                    <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-[#1E3A8A] dark:text-blue-400">Compra de retornables — se agregará al inventario</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <Select value={form.returnable_type_code || "_none"} onValueChange={(v) => setForm({ ...form, returnable_type_code: v === "_none" ? "" : v })}>
                        <SelectTrigger data-testid="purchase-ret-type"><SelectValue placeholder="Tipo de retornable…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">— Elegir —</SelectItem>
                          {retTypes.map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input type="number" min="1" value={form.returnable_qty} onChange={(e) => setForm({ ...form, returnable_qty: e.target.value })} placeholder="Cantidad" data-testid="purchase-ret-qty" />
                    </div>
                  </div>
                </>
              )}
              <div className="md:col-span-3">
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Notas</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" rows={2} />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="purchase-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Registrar compra</Button>
            </div>
          </form>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-100 dark:bg-stone-900 p-1 rounded-lg">
          <TabsTrigger value="compras" data-testid="tab-compras" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Compras varias</TabsTrigger>
          <TabsTrigger value="consolidado" data-testid="tab-consolidado" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Todas las compras</TabsTrigger>
        </TabsList>

        <TabsContent value="compras">
          <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-stone-500" />
              <h3 className="font-outfit font-semibold text-stone-900 dark:text-stone-100">Historial</h3>
              <span className="text-xs text-stone-500 ml-auto">{items.length} compras</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
                  <tr>{["Folio", "Fecha", "Proveedor", "Categoría", "Descripción", "Monto", "Pago", "Vence", "Saldo", ""].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-stone-500">Sin compras registradas.</td></tr>
                  ) : items.map((p) => (
                    <tr key={p.id} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50">
                      <td className="px-3 py-3 text-sm font-medium text-[#1E3A8A] dark:text-blue-400">{p.folio}</td>
                      <td className="px-3 py-3 text-sm text-stone-700 dark:text-stone-300 tabular-nums">{p.purchase_date}</td>
                      <td className="px-3 py-3 text-sm text-stone-800 dark:text-stone-200">{p.supplier_name}</td>
                      <td className="px-3 py-3 text-xs"><span className="inline-block px-2 py-0.5 rounded bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 font-medium">{p.category}</span></td>
                      <td className="px-3 py-3 text-xs text-stone-600 dark:text-stone-400 max-w-[280px] truncate">{p.description}</td>
                      <td className="px-3 py-3 text-sm tabular-nums font-medium text-stone-900 dark:text-stone-100">{money(p.amount)}</td>
                      <td className="px-3 py-3 text-xs">
                        {p.payment_method === "credito"
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 font-medium"><CreditCard className="w-3 h-3" /> {p.credit_days}d</span>
                          : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 font-medium"><Wallet className="w-3 h-3" /> Contado</span>}
                      </td>
                      <td className="px-3 py-3 text-sm tabular-nums text-stone-600 dark:text-stone-400">{p.due_date || "—"}</td>
                      <td className="px-3 py-3 text-sm tabular-nums">
                        {(p.balance || 0) > 0
                          ? <span className="text-amber-700 dark:text-amber-400 font-semibold">{money(p.balance)}</span>
                          : <span className="text-emerald-700 dark:text-emerald-400">Pagada</span>}
                      </td>
                      <td className="px-3 py-3">
                        <button onClick={() => setToDelete(p)} data-testid={`delete-purchase-${p.id}`} className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="consolidado">
          <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden" data-testid="consolidated-purchases-card">
            <div className="px-5 py-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center gap-3 flex-wrap">
              <Layers className="w-4 h-4 text-stone-500" />
              <h3 className="font-outfit font-semibold text-stone-900 dark:text-stone-100">Todas las compras (leche, crema/cuajada, insumos y compras varias)</h3>
              <span className="text-xs text-stone-500">Solo lectura · cada una se edita en su módulo original</span>
              <div className="ml-auto flex items-center gap-3 text-xs">
                <span className="text-stone-500">Total: <b className="text-stone-800 dark:text-stone-200 tabular-nums">{money(consolidated.total_amount)}</b></span>
                {consolidated.total_balance > 0 && <span className="text-amber-700 dark:text-amber-400">Saldo pendiente: <b className="tabular-nums">{money(consolidated.total_balance)}</b></span>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
                  <tr>{["Folio", "Fecha", "Proveedor", "Origen", "Descripción", "Monto", "Pago", "Saldo"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {consolidated.items.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-stone-500">Sin compras registradas todavía.</td></tr>
                  ) : consolidated.items.map((p, i) => (
                    <tr key={`${p.source_type}-${p.folio}-${i}`} data-testid={`consolidated-row-${p.folio}`} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50">
                      <td className="px-3 py-3 text-sm font-medium text-[#1E3A8A] dark:text-blue-400">{p.folio}</td>
                      <td className="px-3 py-3 text-sm text-stone-700 dark:text-stone-300 tabular-nums">{p.date}</td>
                      <td className="px-3 py-3 text-sm text-stone-800 dark:text-stone-200">{p.supplier_name || "—"}</td>
                      <td className="px-3 py-3 text-xs">
                        <span className={`inline-block px-2 py-0.5 rounded font-medium ${p.source_type === "reception" ? "bg-blue-100 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300" : p.source_type === "insumo" ? "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300" : p.source_type === "cream_curd_reception" ? "bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300" : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"}`}>
                          {p.source_type === "purchase" || p.source_type === "cream_curd_reception" ? p.category : CONSOLIDATED_CATEGORY_LABEL[p.source_type]}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs text-stone-600 dark:text-stone-400 max-w-[280px] truncate">{p.description}</td>
                      <td className="px-3 py-3 text-sm tabular-nums font-medium text-stone-900 dark:text-stone-100">{money(p.amount)}</td>
                      <td className="px-3 py-3 text-xs">
                        {p.payment_method === "credito"
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 font-medium"><CreditCard className="w-3 h-3" /> Crédito</span>
                          : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 font-medium"><Wallet className="w-3 h-3" /> Contado</span>}
                      </td>
                      <td className="px-3 py-3 text-sm tabular-nums">
                        {(p.balance || 0) > 0
                          ? <span className="text-amber-700 dark:text-amber-400 font-semibold">{money(p.balance)}</span>
                          : <span className="text-emerald-700 dark:text-emerald-400">Pagada</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar compra {toDelete?.folio}?</AlertDialogTitle><AlertDialogDescription>Los pagos asociados también se eliminarán.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={async () => { try { await api.delete(`/purchases/${toDelete.id}`); toast.success("Compra eliminada"); setToDelete(null); load(); } catch (err) { toast.error(err.response?.data?.detail || "Error"); } }} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
